import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import logo from "../../assets/logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Divider, Grid, useTheme } from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import TollOutlinedIcon from "@mui/icons-material/TollOutlined";
import { useRuntimeConfig } from "../../config/RuntimeConfigProvider";
import {
  HEADER_NAV_ORDER,
  getAuthenticatedNavItemLabel,
  getVisibleAuthenticatedNavItems,
  isAuthenticatedNavItemActive,
  useAuthenticatedAccountActions,
} from "./authenticatedNavigation";
import { useCreditsBalance } from "../../context/CreditsBalanceContext";
import CreditUsageAvatar from "./CreditUsageAvatar";
import AccountMenuCreditsSection from "./AccountMenuCreditsSection";
import { MOBILE_HEADER_HEIGHT_PX, MOBILE_TOUCH_MIN_PX } from "../../theme/mobileLayout";
import MobileNavMenu from "../mobile/MobileNavMenu";

// Keep the AppBar surface white — active state is underline + color only (no lavender wash).
const ActiveTabTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Plus Jakarta Sans",
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: "14px",
  fontStyle: "normal",
  lineHeight: 1.25,
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  padding: "18px 14px 16px 14px",
  marginBottom: "-2px",
  background: "transparent",
  "&:hover": {
    background: "transparent",
  },
}));

const InactiveTabTypography = styled(Typography)(({ theme }) => ({
  fontFamily: "Plus Jakarta Sans",
  color: theme.palette.mode === "dark" ? "#FFFFFF" : "black",
  fontWeight: 700,
  fontSize: "14px",
  fontStyle: "normal",
  lineHeight: 1.25,
  padding: "18px 14px 16px 14px",
  marginBottom: "-2px",
  "&:hover": {
    background:
      theme.palette.mode === "dark"
        ? "radial-gradient(circle, #2A2A2A 0%, #1E1E1E 75%)"
        : "radial-gradient(circle, whitesmoke 0%, white 75%)",
  },
}));

const HeaderAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: "none !important",
  backgroundColor: `${theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF"} !important`,
  backgroundImage: "none !important",
  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#111827",
  borderBottom:
    theme.palette.mode === "dark"
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(88, 89, 91, 0.12)",
  "&.MuiPaper-root": {
    backgroundColor: `${theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF"} !important`,
    backgroundImage: "none !important",
  },
  "&.MuiAppBar-root": {
    backgroundColor: `${theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF"} !important`,
    backgroundImage: "none !important",
  },
}));

const ToolBarStyle = styled(Toolbar)(({ theme }) => ({
  padding: "0px 40px !important",
  minHeight: "56px !important",
  backgroundColor: `${theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF"} !important`,
  backgroundImage: "none !important",
  "@media (max-width: 1200px)": {
    padding: "0px 15px !important",
  },
  [theme.breakpoints.down("md")]: {
    padding: "0px 12px !important",
    minHeight: `${MOBILE_HEADER_HEIGHT_PX}px !important`,
  },
}));

const HeaderIconButton = styled(IconButton)(() => ({
  padding: 6,
  minWidth: MOBILE_TOUCH_MIN_PX,
  minHeight: MOBILE_TOUCH_MIN_PX,
  "& .MuiSvgIcon-root": {
    fontSize: 22,
  },
}));

/** Matches V4 contextual menus (Saved Search edit submenu, filter panels). */
const PROFILE_MENU_ITEM_SX = {
  fontSize: 13,
  fontWeight: 550,
  minHeight: 36,
  px: 1.5,
};

const PROFILE_MENU_VERSION_ITEM_SX = {
  fontSize: 12,
  fontWeight: 400,
  minHeight: 32,
  px: 1.5,
};

export default function Header({ chatNavigationDisabled = false }) {
  const { PUBLIC_VERSION: version } = useRuntimeConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const {
    accountInitial,
    accountName,
    darkMode,
    logout,
    openAccount,
    role,
    toggleDarkMode,
  } = useAuthenticatedAccountActions();

  const theme = useTheme();
  const { balance, loading: creditsLoading, error: creditsError, refreshBalance } =
    useCreditsBalance();
  const visiblePages = getVisibleAuthenticatedNavItems(HEADER_NAV_ORDER, role);

  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (!creditsLoading) {
      refreshBalance();
    }
  };
  const handleOpenHelpCenter = () => {
    navigate("/help");
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleMobileNavOpen = () => {
    if (!creditsLoading) {
      refreshBalance();
    }
    setMobileNavOpen(true);
  };
  const handleAccount = () => {
    setAnchorEl(null);
    openAccount();
  };
  const handleOpenUsage = () => {
    setAnchorEl(null);
    navigate("/settings/usage");
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          py: 0.5,
          minWidth: 248,
          maxWidth: 280,
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          "&:before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      MenuListProps={{ dense: true, sx: { py: 0 } }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        aria-label="Open usage settings"
        aria-describedby="account-menu-credits-help"
        onClick={handleOpenUsage}
        sx={{ display: "block", px: 0, whiteSpace: "normal" }}
      >
        <AccountMenuCreditsSection
          balance={balance}
          loading={creditsLoading}
          error={creditsError}
          onOpenUsage={handleOpenUsage}
          onTopUp={handleOpenUsage}
          embeddedInMenuItem
          helpTextId="account-menu-credits-help"
        />
      </MenuItem>
      <MenuItem
        aria-label="Top Up Credits"
        onClick={handleOpenUsage}
        sx={{
          mx: 1.5,
          my: 0.75,
          minHeight: 36,
          justifyContent: "center",
          gap: 0.75,
          borderRadius: "10px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: 12.5,
          fontWeight: 700,
          "&:hover": { bgcolor: "primary.dark" },
          "&.Mui-focusVisible": { bgcolor: "primary.dark" },
        }}
      >
        <TollOutlinedIcon sx={{ fontSize: "18px !important" }} />
        Top Up Credits
      </MenuItem>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={handleAccount} sx={PROFILE_MENU_ITEM_SX}>
        Account
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleMenuClose();
          logout();
        }}
        sx={PROFILE_MENU_ITEM_SX}
      >
        Log Out
      </MenuItem>
      <Divider />
      <MenuItem disabled sx={PROFILE_MENU_VERSION_ITEM_SX}>
        {`Alphasearch v${version}`}
      </MenuItem>
    </Menu>
  );

  return (
    <HeaderAppBar position="fixed">
      <ToolBarStyle>
        <Grid container justifyContent={"space-between"} alignItems={"center"} rowGap={"8px"}>
          {/* Mobile: compact logo + account avatar with credit-usage ring. */}
          <Grid
            item
            sx={{
              flexGrow: 1,
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => navigate("/landing")}
              aria-label="Go to home"
              sx={{
                border: 0,
                background: "transparent",
                p: 0,
                m: 0,
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                minHeight: MOBILE_TOUCH_MIN_PX,
              }}
            >
              <img
                src={logo}
                alt="AlphaSearch"
                width="96"
                height="18"
                style={{
                  filter: theme.palette.mode === "dark" ? "brightness(0) invert(1)" : "none",
                  display: "block",
                }}
              />
            </Box>
            <HeaderIconButton
              aria-label="Open navigation and account menu"
              onClick={handleMobileNavOpen}
              color="inherit"
              sx={{ overflow: "visible", p: 0.25 }}
            >
              <CreditUsageAvatar
                size="compact"
                initial={accountInitial}
                totalAvailable={balance?.totalAvailable ?? null}
                monthlyAllowance={balance?.effectiveAllowance ?? null}
                remainingRatio={balance?.remainingRatio ?? null}
                status={balance?.status ?? null}
                loading={creditsLoading}
                error={creditsError}
              />
            </HeaderIconButton>
          </Grid>

          {/* Desktop: logo + tabs */}
          <Grid
            item
            sx={{
              display: { xs: "none", md: "flex", cursor: "pointer" },
            }}
          >
            <Grid container columnGap={"24px"} alignItems={"center"} rowGap={"8px"}>
              <Grid
                onClick={() => navigate("/landing")}
                item
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  display: { cursor: "pointer" },
                }}
              >
                <img
                  src={logo}
                  alt="icon"
                  width="148px"
                  height="28px"
                  style={{
                    filter: theme.palette.mode === "dark" ? "brightness(0) invert(1)" : "none",
                  }}
                />
              </Grid>
              <Grid item>
                <Grid container alignItems={"center"}>
                  {visiblePages.map((page) => {
                    const TabTypography = isAuthenticatedNavItemActive(page, location.pathname)
                      ? ActiveTabTypography
                      : InactiveTabTypography;
                    const navigationDisabled = chatNavigationDisabled;
                    const label = getAuthenticatedNavItemLabel(page);
                    return (
                      <Grid
                        item
                        key={page.id}
                        aria-disabled={navigationDisabled ? "true" : undefined}
                        onClick={() => {
                          if (!navigationDisabled) navigate(page.path);
                        }}
                        sx={navigationDisabled ? { cursor: "default", opacity: 0.5 } : undefined}
                      >
                        <TabTypography>{label}</TabTypography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Desktop account chrome */}
          <Grid item sx={{ display: { xs: "none", md: "block" } }}>
            <Grid container columnGap={"8px"} alignItems={"center"}>
              <Grid item>
                <Tooltip title="Help Center">
                  <HeaderIconButton
                    onClick={handleOpenHelpCenter}
                    color="inherit"
                    aria-label="help center"
                  >
                    <HelpOutlineIcon />
                  </HeaderIconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <HeaderIconButton
                  onClick={toggleDarkMode}
                  color="inherit"
                  aria-label="toggle dark mode"
                >
                  {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                </HeaderIconButton>
              </Grid>
              <Grid item>
                <HeaderIconButton
                  onClick={handleProfileMenuOpen}
                  aria-label="account menu"
                  sx={{ overflow: "visible" }}
                >
                  <CreditUsageAvatar
                    initial={accountInitial}
                    totalAvailable={balance?.totalAvailable ?? null}
                    monthlyAllowance={balance?.effectiveAllowance ?? null}
                    remainingRatio={balance?.remainingRatio ?? null}
                    status={balance?.status ?? null}
                    loading={creditsLoading}
                    error={creditsError}
                  />
                </HeaderIconButton>
                {renderMenu}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ToolBarStyle>

      {mobileNavOpen ? (
        <MobileNavMenu
          open
          onClose={() => setMobileNavOpen(false)}
          onNavigate={(path) => navigate(path)}
          pathname={location.pathname}
          role={role}
          darkMode={darkMode}
          onToggleDarkMode={toggleDarkMode}
          accountInitial={accountInitial}
          accountName={accountName}
          onOpenAccount={openAccount}
          onLogout={logout}
          navigationDisabled={chatNavigationDisabled}
          creditsBalance={balance}
          creditsLoading={creditsLoading}
          creditsError={creditsError}
          onRefreshCredits={refreshBalance}
          version={version}
        />
      ) : null}
    </HeaderAppBar>
  );
}
